import { MIME_TYPES } from '@mantine/dropzone';
import { t } from 'i18next';
import _ from 'lodash';
import moment from 'moment';

// Language constants
export const LANGUAGES = {
  EN: 'EN',
  RO: 'RO',
  RU: 'RU',
};

export const SUPPORTED_LANGUAGES = [LANGUAGES.EN, LANGUAGES.RO, LANGUAGES.RU];

export const DEFAULT_LANGUAGE = LANGUAGES.EN;

export const avoidRedirectRoots = [
  '/login',
  '/forgot',
  '/offer-preview',
  '/candidate-offer',
  '/reset-password',
  '/choose-password',
];

export const getFrequencyValues = () => [
  { value: '0', label: t('keywords.one-time') },
  { value: '3', label: t('keywords.every-three-months') },
  { value: '6', label: t('keywords.two-times-year') },
  { value: '12', label: t('keywords.every-year') },
  { value: '18', label: t('keywords.once-in-eighteen-months') },
  { value: '24', label: t('keywords.every-two-years') },
];

export const payFrequencyNames = {
  10: 'keywords.monthly',
};

export const questionTypes = [
  { value: 'text', label: 'Text' },
  { value: 'select', label: 'Select' },
  { value: 'options', label: 'Options' },
];

export const getSurveys = () => ({
  assessment: { value: '10', label: t('keywords.performance-assessment') },
  upwardFeedback: { value: '11', label: t('keywords.upward-feedback') },
  manager: { value: '12', label: t('keywords.manager-assessment') },
  peerFeedback: { value: '13', label: t('keywords.peer-feedback') },
  candidate: { value: '20', label: t('keywords.candidate') },
  other: { value: '100', label: t('keywords.other') },
});

export const getSurveyStatuses = () => [
  { value: '0', label: t('keywords.draft') },
  { value: '1', label: t('keywords.active') },
  { value: '2', label: t('keywords.archived') },
];

export const surveyStatusesObj = {
  Draft: getSurveyStatuses()[0].value,
  Active: getSurveyStatuses()[1].value,
  Archived: getSurveyStatuses()[2].value,
};

export const surveysSelectResponses = {
  'Strongly Disagree': 1,
  Disagree: 2,
  Neutral: 3,
  Agree: 4,
  'Strongly Agree': 5,
};

export const performanceCycleStatuses = {
  Draft: '0',
  Active: '1',
  Disabled: '2',
};

export const getPerformanceCycleStatuses = () => [
  { value: performanceCycleStatuses.Draft, label: t('keywords.draft') },
  { value: performanceCycleStatuses.Active, label: t('keywords.active') },
  { value: performanceCycleStatuses.Disabled, label: t('keywords.disabled') },
];

export const getRequestTypes = () => [
  { value: 'assets', label: t('keywords.asset-request') },
  { value: 'compensation', label: t('settings.tabs.compensation') },
  { value: 'status', label: t('keywords.employment-status') },
  { value: 'jobInfo', label: t('keywords.job-information') },
  { value: 'promotion', label: t('keywords.promotion') },
];

export const boardingListTypes = {
  onboarding: 1,
  offboarding: 0,
};

export const variablePattern = /\{([^}]+)\}/;
export const variablePatternG = /\{([^}]+)\}/g;

export const offerStatuses = {
  Sent: 1,
  Signed: 3,
  Archived: 4,
};

export const requestsCategories = {
  'Time Off': 'timeOffId',
  Performance: 'performanceId',
  Signature: 'signatureId',
  Asset: 'assetId',
  Compensation: 'compensationId',
  EmploymentStatus: 'employmentStatusId',
  JobInformation: 'jobInformationId',
  Promotion: 'promotionId',
};

export const requestStatuses = {
  Pending: 'pending',
  Approved: 'approved',
  Denied: 'denied',
  Deleted: 'deleted',
  Completed: 'completed',
  Signed: 'signed',
};

export const requestStatusesVal = {
  Pending: 0,
  Approved: 10,
  Denied: 20,
  Deleted: 30,
  Completed: 40,
};

export const requestPerformanceTypes = {
  choice: 'choiceable',
};

export const performanceCycleDateTypes = {
  fixedDate: 0,
  hireDate: 1,
};

export const getStartAssessment = () =>
  Array.from({ length: 4 })
    .map((val, index) => ({
      value: `${index + 1}`,
      label: `${index + 1} ${
        index > 1 ? t('keywords.months') : t('keywords.month').toLowerCase()
      } ${t('keywords.after-employee-hire-date')}`,
    }))
    .concat({
      value: '12',
      label: t('keywords.one-year-after-employee-hire-date'),
    });

export const getCelebrationTypes = () => ({
  birthday: t('keywords.happy-birthday'),
  anniversary: t('keywords.congratulations-your-anniversary'),
});

export const employeeStatuses = {
  All: '',
  Active: '1',
  Inactive: '0',
};

export const getEmployeeStatusesLabels = () => ({
  All: t('keywords.all'),
  Active: t('keywords.active'),
  Inactive: t('keywords.inactive'),
});

export const getEmployeeCompensationTypeLabels = () => ({
  1: t('keywords.salary'),
  2: t('keywords.hourly'),
  3: t('keywords.commission-only'),
});

export const getEmployeeGenderLabels = () => ({
  Male: t('keywords.male'),
  Female: t('keywords.female'),
});

export const responseTypesLabels = {
  promoters: 'keywords.promoters',
  neutrals: 'keywords.neutrals',
  detractors: 'keywords.detractors',
};

export const rolesLabels = {
  superadmin: 'keywords.promoters',
  HR: 'keywords.hr',
  employee: 'keywords.employee',
  manager: 'keywords.manager',
  no_role: 'keywords.no_role',
};

export const additionalFieldsLabels = {
  cover_letter: 'keywords.cover-letter',
  resume: 'keywords.resume',
  date_available: 'keywords.date-available',
  desired_salary: 'keywords.desired-salary',
  linkedin_prof: 'keywords.linked-profile-url',
  website_blog: 'keywords.website-blog-url',
  social_media_1: 'keywords.social-media-first',
  social_media_2: 'keywords.social-media-sec',
  highest_educ: 'keywords.highest-education',
  who_referred: 'keywords.who-referred-position',
  references: 'keywords.references',
  who_hear_us: 'keywords.how-hear-about-us',
  why_work_here: 'keywords.why-want-work-here',
  what_can_bring: 'keywords.what-you-bring-to-company',
};

export const satisfactionStatusesLabels = {
  Stopped: 'keywords.stopped',
  Active: 'keywords.active',
  Sent: 'keywords.sent',
  Closed: 'keywords.closed',
  Draft: 'keywords.draft',
};

export const lookupsModels = {
  country: 'Country',
  timezone: 'Timezone',
  location: 'Location',
  jobTitle: 'JobTitle',
  department: 'Department',
  division: 'Division',
  language: 'Language',
  compensation: 'EmployeeCompensation',
  compensationType: 'EmployeeCompensationType',
  compensationPaySchedule: 'CompensationPaySchedule',
  reason: 'Reason',
  surveyResponse: 'SurveyResponse',
  survey: 'Survey',
  employeeStatus: 'EmployeeStatus',
  assetCategory: 'AssetCategory',
  link: 'Link',
  announcement: 'Announcement',
  job: 'Job',
  jobApplicationFields: 'JobApplicationFields',
  satisfactionSurvey: 'SatisfactionSurvey',
  employeeGender: 'EmployeeGender',
  employee: 'Employee',
  employeeAgeRange: 'EmployeeAgeRange',
  employeeEngagedRange: 'EmployeeEngagedRange',
  workPattern: 'WorkPattern',
  extraPay: 'ExtraPay',
  general: 'GeneralSettings',
};

export const getLookupsLabels = () => ({
  [lookupsModels.country]: t('keywords.country'),
  [lookupsModels.timezone]: t('keywords.timezone'),
  [lookupsModels.location]: t('keywords.location'),
  [lookupsModels.jobTitle]: t('keywords.job-title'),
  [lookupsModels.department]: t('keywords.department'),
  [lookupsModels.division]: t('keywords.division'),
  [lookupsModels.language]: t('keywords.language'),
  [lookupsModels.employeeStatus]: t('keywords.employee-status'),
  [lookupsModels.assetCategory]: t('keywords.asset-category'),
  [lookupsModels.link]: t('keywords.link'),
  [lookupsModels.announcement]: t('keywords.announcement'),
  [lookupsModels.job]: t('keywords.job'),
  [lookupsModels.jobApplicationFields]: t('keywords.job-application-fields'),
  [lookupsModels.satisfactionSurvey]: t('keywords.satisfaction-survey'),
  [lookupsModels.employeeGender]: t('keywords.gender'),
  [lookupsModels.employeeAgeRange]: t('keywords.age-range'),
  [lookupsModels.employeeEngagedRange]: t('keywords.engaged-range'),
  [lookupsModels.workPattern]: t('keywords.work-pattern'),
  [lookupsModels.extraPay]: t('keywords.extra-pay'),
  [lookupsModels.compensationType]: t('keywords.compensation-type'),
});

export const lookupsFilters = {
  [lookupsModels.employeeStatus]: {
    onlyActive: 'onlyActive',
    getTerminationFields: 'getTerminationFields',
  },
};

export const getWeekDaysLabels = () => [
  { value: 'Sunday', label: t('days.sunday') },
  { value: 'Monday', label: t('days.monday') },
  { value: 'Tuesday', label: t('days.tuesday') },
  { value: 'Wednesday', label: t('days.wednesday') },
  { value: 'Thursday', label: t('days.thursday') },
  { value: 'Friday', label: t('days.friday') },
  { value: 'Saturday', label: t('days.saturday') },
];

export const weekDaysLabels = {
  Sunday: 'days.sunday',
  Monday: 'days.monday',
  Tuesday: 'days.tuesday',
  Wednesday: 'days.wednesday',
  Thursday: 'days.thursday',
  Friday: 'days.friday',
  Saturday: 'days.saturday',
};

export const personalBlocksName = {
  1: 'keywords.personal',
  2: 'keywords.contact-information',
  3: 'keywords.address',
  4: 'keywords.job-related',
  5: 'keywords.social',
};

export const employeeSchemaLabels = {
  id: 'keywords.employee-id',
  first_name: 'keywords.first-name',
  middle_name: 'keywords.middle-name',
  last_name: 'keywords.last-name',
  preferred_name: 'keywords.preferred-name',
  dob: 'keywords.date-birth',
  gender: 'keywords.gender',
  marital: 'keywords.marital-status',
  nationality: 'keywords.nationality',
  birthplace: 'keywords.birthplace',
  ssn: 'keywords.social-security-number',
  tax_no: 'keywords.tax-number',
  national_id: 'keywords.national-id',
  nin: 'keywords.nin',
  shirt_size: 'keywords.shirt-size',
  tshirt_size: 'keywords.t-shirt-size',
  jacket_size: 'keywords.jacket-size',
  allergies: 'keywords.allergies',
  dietary: 'keywords.dietary-restrictions',
  citizenship: 'keywords.citizenship',
  email: 'keywords.email',
  phone_work: 'keywords.work-phone',
  phone_work_ext: 'keywords.work-phone-extension',
  phone_mobile: 'keywords.mobile-phone',
  phone_home: 'keywords.home-phone',
  email_home: 'keywords.home-email',
  address_line1: 'keywords.address-line-one',
  address_line2: 'keywords.address-line-sec',
  address_city: 'keywords.city',
  address_state: 'keywords.state',
  address_zip: 'keywords.zip',
  country_id: 'keywords.country',
  hire_date: 'keywords.hire-date',
  jobTitle: 'keywords.job-title',
  sc_linkedin: 'keywords.linkedin-profile',
  sc_twitter: 'keywords.twitter-profile',
  sc_facebook: 'keywords.facebook-profile',
  sc_instagram: 'keywords.instagram-profile',
};

export const personalOptionsValues = {
  gender: { male: 'keywords.male', female: 'keywords.female' },
  marital: {
    married: 'keywords.married',
    single: 'keywords.single',
    common_law: 'keywords.common_law',
    domestic_partnership: 'keywords.domestic-partnership',
  },
};

export const siteName = 'AM2';

export const hideForNow = false;

export const getTimeOffPolicyScheduleType = () => ({
  weekly: t('keywords.weekly'),
  twice_a_month: t('keywords.twice-month'),
  monthly: t('keywords.monthly'),
  quarterly: t('keywords.quarterly'),
  twice_a_year: t('keywords.twice-year'),
  yearly: t('keywords.yearly'),
});

export const acceptTypes = {
  allImages: 'image/*',
  currentValidImages: 'image/jpeg,image/png,image/jpg',
};

export const datePeriods = {
  fullMonth: ({
    maxLength = 28,
    withLastDayValue = true,
    startFrom = 0,
    lastDateValue = '-1',
  } = {}) => {
    const days = _.map(
      _.range(startFrom + 1, startFrom + maxLength + 1),
      (day) => ({
        label: t('keywords.ordinal-day', { count: day, ordinal: true }),
        value: String(day),
      }),
    );

    return withLastDayValue
      ? _.concat(days, { label: t('keywords.last-day'), value: lastDateValue })
      : days;
  },
};

export const tabNames = {
  dashboard: 'dashboard',
  notifications: 'notifications',
  administration: 'administration',
  permissions: 'permissions',
  globalSearch: 'search',
  settings: 'settings',
  settingsSurveys: 'settings-surveys',
  settingsPerformanceManagement: 'settings-performance-management',
  reports: 'reports',
  requests: 'requests',
  editTalentGroup: 'edit-talent-group',
  createTalentGroup: 'create-talent-group',
  hiringTalentGroup: 'hiring-talent-group',
  hiringJobCandidate: 'hiring-job-candidate',
  editHiringJob: 'edit-hiring-job',
  createHiringJob: 'create-hiring-job',
  hiringJob: 'hiring-job',
  hiring: 'hiring',
  editEmployees: 'edit-employee',
  createEmployees: 'create-employee',
  employeesProfile: 'employee-profile',
  employees: 'employees',
  myProfile: 'my-profile',
  userProfile: 'user-profile',
  recruitment: 'recruitment',
  hr: 'hr',
  files: 'files',
  calendar: 'calendar',
  announcements: 'announcements',
  createAnnouncement: 'create-announcement',
  editAnnouncement: 'edit-announcement',
  payroll: 'payroll',
  payrollReport: 'payroll-report',
};

export const userStatuses = {
  all: 'All',
  active: 'Active',
  inactive: 'Inactive',
};

export const userStatusMeta = {
  [userStatuses.all]: '',
  [userStatuses.active]: 1,
  [userStatuses.inactive]: 0,
};

export const userStatusesList = [
  userStatuses.all,
  userStatuses.active,
  userStatuses.inactive,
];

export const oauthAPI = {
  microsoftLogin: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
};

export const authProviders = {
  microsoft: 'microsoft',
  google: 'google',
};

export const fileTypes = {
  directory: 'd',
  file: 'f',
};

export const filterEmployeeTypes = {
  someEmployee: '1',
  all: '0',
};

export const fileStatuses = {
  readable: 0,
  assignable: 1,
};

export const employeeStatusTypes = {
  terminated: 0,
  system: 1,
  custom: 2,
};

export const employeeReasons = {
  termination: '30',
  extrapay: '20',
};

export const calendarEventTypes = {
  birthdays: 'birthdays',
  anniversaries: 'anniversaries',
  timeoff: 'time_off_records',
  holiday: 'holiday',
};

export const getCalendarEventTypeLabels = () => ({
  [calendarEventTypes.birthdays]: t('keywords.birthdays'),
  [calendarEventTypes.anniversaries]: t('keywords.anniversaries'),
  [calendarEventTypes.timeoff]: t('payroll-report.payment-table.time-off'),
  [calendarEventTypes.holiday]: t('keywords.holiday'),
});

export const WidgetsTypes = {
  celebrations: 'celebrations',
  whoOut: 'who_out',
  companyHolidays: 'company_holidays',
  welcomeAtomate: 'welcome_atomate',
  candidatesOffers: 'candidates_offers',
  incompleteTrainings: 'incomplete_trainings',
  timeoffRequests: 'time_off_requests',
  companyLinks: 'company_links',
};

// in ms
export const Time = {
  WidgetStaleQueryTime: 10000,
};

export const AnnouncementStatuses = {
  Published: '10',
  PublishedAndEmail: '11',
  Draft: '0',
};

export const announcementStatusesNames = {
  [AnnouncementStatuses.Published]: 'keywords.published',
  [AnnouncementStatuses.PublishedAndEmail]: 'keywords.published-and-email',
  [AnnouncementStatuses.Draft]: 'keywords.draft',
};

export const jobFieldTypes = {
  text: 1,
  date: 2,
  file: 3,
  textArea: 4,
  link: 5,
};

export const jobQuestionFieldType = {
  text: 'text',
  select: 'select',
  option: 'options',
};

export const requestsTypes = {
  inbox: 'inbox',
  sent: 'sent',
  signature: 'signature',
  timeoff: 'timeoff',
  performance: 'performance',
  performance_completed: 'performance_completed',
  done: 'done',
  saved: 'saved',
};

export const uploadFileTypes = {
  images: [MIME_TYPES.png, MIME_TYPES.jpeg],
  pdf: MIME_TYPES.pdf,
  rtf: 'application/rtf',
  rtf2: 'text/rtf',
  docs: [MIME_TYPES.doc, MIME_TYPES.docx],
  zip: MIME_TYPES.zip,
  rtfs() {
    return [this.rtf, this.rtf2];
  },
  all() {
    return [...this.images, this.pdf, ...this.docs, this.zip, ...this.rtfs()];
  },
  allExZip() {
    return [...this.images, this.pdf, ...this.docs, ...this.rtfs()];
  },
};

export const satisfactionStatuses = {
  Active: 1,
  Stopped: 0,
  Sended: 2,
  Draft: 4,
};

export const payrollDayTypes = {
  default: 10,
  holiday: 20,
  timeoff: 30,
  weekend: 40,
};

export const payrollDayTypesLabels = {
  default: 'keywords.default',
  holiday: 'keywords.holiday',
  timeoff: 'settings.tabs.timeOff',
  weekend: 'keywords.weekend',
};

export const payrollStatuses = {
  planned: '10',
  due: '20',
  overdue: '30',
  saved: '40',
  finalized: '50',
};

export const payrollStatusesNames = {
  [payrollStatuses.planned]: 'keywords.planned',
  [payrollStatuses.due]: 'keywords.due',
  [payrollStatuses.overdue]: 'keywords.overdue',
  [payrollStatuses.saved]: 'keywords.saved',
  [payrollStatuses.finalized]: 'keywords.finalized',
};

export const getMonthNames = () => [
  { value: 'january', label: t('keywords.january') },
  { value: 'february', label: t('keywords.february') },
  { value: 'march', label: t('keywords.march') },
  { value: 'april', label: t('keywords.april') },
  { value: 'may', label: t('keywords.may') },
  { value: 'june', label: t('keywords.june') },
  { value: 'july', label: t('keywords.july') },
  { value: 'august', label: t('keywords.august') },
  { value: 'september', label: t('keywords.september') },
  { value: 'october', label: t('keywords.october') },
  { value: 'november', label: t('keywords.november') },
  { value: 'december', label: t('keywords.december') },
];

export const timeSheetColors = {
  [payrollDayTypes.holiday]: 'green.0',
  [payrollDayTypes.timeoff]: 'blue.0',
  [payrollDayTypes.weekend]: 'yellow.1',
};

export const defaultWeekdays = Array.from({ length: 7 }).map((v, i) =>
  moment(i, 'e')
    .startOf('week')
    .isoWeekday(i + 1)
    .format('dddd'),
);

export const workPatternFrequencies = {
  weekly: '10',
  biWeekly: '20',
  flexible: '30',
};

export const workPatternFrequenciesNames = {
  [workPatternFrequencies.weekly]: 'keywords.weekly',
  [workPatternFrequencies.biWeekly]: 'keywords.bi-weekly',
  [workPatternFrequencies.flexible]: 'keywords.flexible',
};

export const exportFileTypes = {
  csv: '10',
  excel: '20',
};

export const hiringStatuses = {
  All: '',
  Draft: '1',
  Opened: '2',
  'On Hold': '3',
  Filled: '4',
  Cancelled: '5',
};

export const extraPayStatuses = {
  paid: 10,
  unpaid: 0,
};

export const extraPayStatusesNames = {
  [extraPayStatuses.paid]: 'extra-pay-status.paid',
  [extraPayStatuses.unpaid]: 'extra-pay-status.unpaid',
};

export const extraPayTypes = {
  bonus: 0,
  commission: 10,
  reimbursement: 20,
  other: 30,
};

export const extraPayTypesNames = {
  [extraPayTypes.bonus]: 'extra-pay-types.bonus',
  [extraPayTypes.commission]: 'extra-pay-types.commission',
  [extraPayTypes.reimbursement]: 'extra-pay-types.reimbursement',
  [extraPayTypes.other]: 'extra-pay-types.other',
};

export const withoutTrashedParam = 'withoutTrashed';
export const withCountParam = 'withCount';
